import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-hot-toast';
import {
  Activity,
  ArrowRight,
  Briefcase,
  DollarSign,
  Flag,
  Mail,
  MessageSquare,
  Settings,
  Siren,
  Smartphone,
  UserPlus,
  Users,
} from 'lucide-react';
import { useCockpitTitle } from '../components/EmployerCockpitLayout';
import { SIGNALEN_KEY } from '../components/EmployerCockpitSidebar';
import { initSocket } from '../services/socketService';
import api from '../services/api';
import { employerCockpitApi, KANALEN_KEY, SIGNAAL_VENSTERS, type SignaalVenster } from '../services/employerCockpitApi';

interface Signal {
  id: string;
  key: string;
  source: 'afgeleid' | 'notificatie';
  category: SignaalVenster;
  severity: 'urgent' | 'actie' | 'info';
  params: Record<string, string | number | null>;
  title?: string;
  content?: string;
  deeplink: string;
  occurredAt: string;
  isRead: boolean;
}

interface SignalenResponse {
  signalen: Signal[];
  tellers: Partial<Record<SignaalVenster, { totaal: number; ongelezen: number }>>;
  ongelezen: number;
}

/** Per venster: icoon en de analysemodule waar een klik op het venster naartoe gaat. */
const VENSTER: Record<SignaalVenster, { icon: React.ComponentType<{ className?: string }>; module: string }> = {
  crisis: { icon: Siren, module: '/employer/data/crisis' },
  trajecten: { icon: Activity, module: '/employer/data/verzuim' },
  financien: { icon: DollarSign, module: '/employer/data/budgetten' },
  samenwerkingen: { icon: Users, module: '/employer/data/pool' },
  medewerkers: { icon: UserPlus, module: '/employer/data/cultuur' },
  projecten: { icon: Flag, module: '/employer/trajecten/mijlpalen' },
  opdrachten: { icon: Briefcase, module: '/employer/dashboard/zoeken?route=vacatures' },
  berichten: { icon: MessageSquare, module: '/berichten?tab=opdrachtgever' },
  account: { icon: Settings, module: '/employer/mijn-belagom' },
};

const STIP: Record<Signal['severity'], string> = { urgent: 'bg-red-500', actie: 'bg-amber-400', info: 'bg-blue-400' };
const ZICHTBAAR_PER_VENSTER = 3;

/** Schakelaar in de stijl [ SMS ] | [ E-mail ]: aan = navy, uit = grijs (gelijk aan de professional-kant). */
const KanaalKnop: React.FC<{
  aan: boolean;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  uitgeschakeld: boolean;
  titel?: string;
  onClick: () => void;
}> = ({ aan, label, icon: Icon, uitgeschakeld, titel, onClick }) => (
  <button
    type="button"
    role="switch"
    aria-checked={aan}
    disabled={uitgeschakeld}
    title={titel}
    onClick={onClick}
    className={`inline-flex items-center gap-1.5 rounded-md px-2.5 py-1 text-xs font-semibold border transition-colors disabled:opacity-60 ${
      aan ? 'bg-navy-900 border-navy-900 text-white' : 'bg-white border-gray-300 text-navy-400 hover:border-navy-300'
    }`}
  >
    <Icon className="w-3.5 h-3.5" />
    {label}
  </button>
);

/**
 * Dashboard › Alle signalen: Admin-stijl vensters per signaaltype. Een klik op
 * het venster opent de analysemodule; elk venster heeft twee onafhankelijke
 * grendels (SMS en e-mail) in company_notification_settings.
 */
export const EmployerSignalenPage: React.FC = () => {
  const { t } = useTranslation('employerDashboard');
  const { t: tEmp } = useTranslation('employer');
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [uitgeklapt, setUitgeklapt] = useState<SignaalVenster | null>(null);
  useCockpitTitle(t('signalen.titel'), t('signalen.ondertitel'));

  const { data, isLoading, isError } = useQuery<SignalenResponse>({
    queryKey: SIGNALEN_KEY,
    queryFn: async () => (await api.get('/employer/dashboard/signalen')).data.data,
    refetchInterval: 30_000,
    refetchOnWindowFocus: true,
  });
  const { data: kanaalData } = useQuery({ queryKey: KANALEN_KEY, queryFn: employerCockpitApi.kanalen });

  // Realtime: notificaties en chatberichten komen via de socket binnen.
  useEffect(() => {
    const socket = initSocket();
    if (!socket) return;
    const ververs = () => queryClient.invalidateQueries({ queryKey: SIGNALEN_KEY });
    socket.on('new_notification', ververs);
    socket.on('new_message', ververs);
    return () => {
      socket.off('new_notification', ververs);
      socket.off('new_message', ververs);
    };
  }, [queryClient]);

  const schakel = useMutation({
    mutationFn: (v: { venster: SignaalVenster; kanaal: 'sms' | 'email'; aan: boolean }) =>
      employerCockpitApi.zetKanaal(v.venster, { [v.kanaal]: v.aan }),
    onMutate: async (v) => {
      await queryClient.cancelQueries({ queryKey: KANALEN_KEY });
      const vorige = queryClient.getQueryData<typeof kanaalData>(KANALEN_KEY);
      queryClient.setQueryData<typeof kanaalData>(KANALEN_KEY, (oud) =>
        oud ? { ...oud, kanalen: { ...oud.kanalen, [v.venster]: { ...oud.kanalen[v.venster], [v.kanaal]: v.aan } } } : oud
      );
      return { vorige };
    },
    onError: (_e, _v, ctx) => {
      if (ctx?.vorige) queryClient.setQueryData(KANALEN_KEY, ctx.vorige);
      toast.error(t('signalen.opslaanFout'));
    },
    onSuccess: (antwoord, v) => {
      queryClient.setQueryData(KANALEN_KEY, antwoord);
      toast.success(
        t(v.aan ? 'signalen.kanaalAan' : 'signalen.kanaalUit', {
          kanaal: v.kanaal === 'sms' ? 'SMS' : t('signalen.email'),
          venster: t(`signalen.venster.${v.venster}.titel`),
        })
      );
    },
  });

  const perVenster = useMemo(() => {
    const m = new Map<SignaalVenster, Signal[]>();
    for (const s of data?.signalen ?? []) m.set(s.category, [...(m.get(s.category) ?? []), s]);
    return m;
  }, [data?.signalen]);

  const openSignaal = (s: Signal) => {
    if (!s.isRead) {
      // Optimistisch op gelezen; het doorsturen wacht niet op de server.
      queryClient.setQueryData<SignalenResponse>(SIGNALEN_KEY, (oud) =>
        oud
          ? { ...oud, ongelezen: Math.max(0, oud.ongelezen - 1), signalen: oud.signalen.map((x) => (x.id === s.id ? { ...x, isRead: true } : x)) }
          : oud
      );
      api.post('/employer/dashboard/signalen/gelezen', { signal_id: s.id }).catch(() => queryClient.invalidateQueries({ queryKey: SIGNALEN_KEY }));
    }
    navigate(s.deeplink);
  };

  const signaalTitel = (s: Signal) => {
    if (s.source === 'notificatie') return s.title ?? '';
    const params = { ...s.params } as Record<string, any>;
    for (const veld of ['bedrag', 'tarief']) {
      if (typeof params[veld] === 'number') {
        params[veld] = new Intl.NumberFormat('nl-NL', { style: 'currency', currency: 'EUR' }).format(params[veld]);
      }
    }
    // Ontbrekende naam (verwijderd account) mag geen lege plek in de zin geven.
    if (!params.professional) params.professional = tEmp('signaalDashboard.onbekendeProfessional');
    return String(tEmp(`signaalDashboard.signalen.${s.key}.titel`, params));
  };

  const magWijzigen = !!kanaalData?.mag_wijzigen;

  return (
    <div className="p-4 sm:p-6">
      {isError && <p className="mb-3 text-sm text-red-600">{t('signalen.laadFout')}</p>}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {SIGNAAL_VENSTERS.map((venster) => {
          const { icon: Icon, module } = VENSTER[venster];
          const signalen = perVenster.get(venster) ?? [];
          const ongelezen = data?.tellers?.[venster]?.ongelezen ?? 0;
          const kanalen = kanaalData?.kanalen[venster];
          const bezig = schakel.isPending && schakel.variables?.venster === venster;
          const toonAlle = uitgeklapt === venster;
          const zichtbaar = toonAlle ? signalen : signalen.slice(0, ZICHTBAAR_PER_VENSTER);
          return (
            <section key={venster} className="rounded-xl border border-gray-200 bg-white shadow-sm flex flex-col">
              <button
                type="button"
                onClick={() => navigate(module)}
                className="group flex items-start gap-3 px-4 pt-4 pb-3 border-b border-gray-100 text-left hover:bg-navy-50/40 rounded-t-xl transition-colors"
              >
                <span className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${venster === 'crisis' ? 'bg-red-50' : 'bg-navy-50'}`}>
                  <Icon className={`w-5 h-5 ${venster === 'crisis' ? 'text-red-600' : 'text-navy-900'}`} />
                </span>
                <span className="flex-1 min-w-0">
                  <span className="text-sm font-semibold text-navy-900 flex items-center gap-2">
                    {t(`signalen.venster.${venster}.titel`)}
                    {ongelezen > 0 && (
                      <span className="inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1.5 rounded-full bg-gold-400 text-navy-900 text-xs font-bold">
                        {ongelezen}
                      </span>
                    )}
                  </span>
                  <span className="block text-xs text-navy-500 mt-0.5">{t(`signalen.venster.${venster}.omschrijving`)}</span>
                </span>
                <ArrowRight className="w-4 h-4 mt-1 text-gray-300 group-hover:text-navy-900 flex-shrink-0" aria-label={t('signalen.openModule')} />
              </button>

              <div className="flex items-center justify-between gap-2 px-4 py-2 bg-navy-50/50 border-b border-gray-100">
                <span className="text-xs font-medium text-navy-500">{t('signalen.meldMij')}</span>
                <div className="flex items-center gap-1.5">
                  <KanaalKnop
                    aan={!!kanalen?.sms}
                    label="SMS"
                    icon={Smartphone}
                    uitgeschakeld={!kanalen || !magWijzigen || bezig}
                    titel={magWijzigen ? undefined : t('signalen.kanalenAlleenAdmin')}
                    onClick={() => schakel.mutate({ venster, kanaal: 'sms', aan: !kanalen?.sms })}
                  />
                  <KanaalKnop
                    aan={!!kanalen?.email}
                    label={t('signalen.email')}
                    icon={Mail}
                    uitgeschakeld={!kanalen || !magWijzigen || bezig}
                    titel={magWijzigen ? undefined : t('signalen.kanalenAlleenAdmin')}
                    onClick={() => schakel.mutate({ venster, kanaal: 'email', aan: !kanalen?.email })}
                  />
                </div>
              </div>

              <div className="p-3 flex-1">
                {isLoading ? (
                  <div className="h-12 rounded-lg bg-gray-50 animate-pulse" />
                ) : signalen.length === 0 ? (
                  <p className="text-xs text-navy-400 px-1 py-3">{t('signalen.geenSignalen')}</p>
                ) : (
                  <>
                    <ul className="space-y-1">
                      {zichtbaar.map((s) => (
                        <li key={s.id}>
                          <button
                            type="button"
                            onClick={() => openSignaal(s)}
                            className={`w-full flex items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm hover:bg-gray-50 ${
                              s.isRead ? 'text-navy-500' : 'font-medium text-navy-900'
                            }`}
                          >
                            <span className={`w-2 h-2 rounded-full flex-shrink-0 ${s.isRead ? 'bg-gray-300' : STIP[s.severity]}`} />
                            <span className="truncate">{signaalTitel(s)}</span>
                          </button>
                        </li>
                      ))}
                    </ul>
                    {signalen.length > ZICHTBAAR_PER_VENSTER && (
                      <button
                        type="button"
                        onClick={() => setUitgeklapt(toonAlle ? null : venster)}
                        className="mt-2 px-2 text-xs font-semibold text-navy-700 hover:text-navy-900"
                      >
                        {toonAlle ? t('signalen.minder') : t('signalen.toonAlle', { count: signalen.length })}
                      </button>
                    )}
                  </>
                )}
              </div>
            </section>
          );
        })}
      </div>
    </div>
  );
};
